import React, { useState, createContext, useContext, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useRouter } from 'next/router';
import { getQueryStringParams, addQueryStringParam } from '@/Framework/url/helpers/queryString';
import { initialFiltersState } from './Bonds/constants';

export interface IParamsInitial {
  sortBy?: string,
  sortOrder?: string,
  page?: number,
  perPage?: number,
  search?: string,
}

interface IConfig {
  updateLocation?: boolean,
  baseParams?: IParamsInitial,
}

interface ISectorItem {
  sectorId: string,
  subsectorId: string,
}

const stateSelector = (state) => ({
  router: state.router,
});

export const useFilters = (initial?: IConfig, sectorItem?: ISectorItem) => {
  const [params, setParams] = useState<IParamsInitial>(initial?.baseParams || initialFiltersState);
  const { router: { location } } = useSelector(stateSelector);
  const { replace } = useRouter();

  const handlePageChange = (page: number) => {
    setParams((prevParams) => ({ ...prevParams, page }));
  };

  const handleSortChange = (sortBy: string, sortOrder: string) => {
    setParams((prevParams) => ({ ...prevParams, sortBy, sortOrder }));
  };

  const handlePerPageChange = (perPage: number) => {
    setParams((prevParams) => ({ ...prevParams, page: 1, perPage }));
  };

  const onSearch = (search: string) => {
    setParams((prevParams) => ({ ...prevParams, page: 1, search }));
    if (!initial.updateLocation) return;
    let locationSearch = addQueryStringParam(location, { q: search });
    replace(`${ location.pathname }?${ locationSearch }`, undefined, { shallow: true });
  };

  useEffect(() => {
    const queryParams = getQueryStringParams(location);
    if ('q' in queryParams) {
      setParams((prevParams) => ({ ...prevParams, search: queryParams.q }));
    }
  }, []);

  useEffect(() => {
    if (sectorItem?.sectorId && sectorItem?.subsectorId) {
      setParams((prevParams) => ({ ...prevParams, search: '', page: 1 }));
    }
  }, [sectorItem?.sectorId, sectorItem?.subsectorId]);

  return {
    handlePageChange,
    handleSortChange,
    handlePerPageChange,
    onSearch,
    params,
  };
};

type FiltersContextType = ReturnType<typeof useFilters>;

export const FiltersContext = createContext<FiltersContextType>(null);

export const useFiltersContext = () => {
  const context = useContext(FiltersContext);
  if (!context) {
    throw new Error('useFiltersContext must be used within the FiltersContext');
  }
  return context;
};

interface IProps {
  children: React.ReactNode,
  initial?: IConfig,
  sectorItem?: ISectorItem,
}

const FiltersContextProvider = ({ initial, sectorItem, children }: IProps) => (
  <FiltersContext.Provider value={ useFilters(initial, sectorItem) }>
    { children }
  </FiltersContext.Provider>
);

export default FiltersContextProvider;
